import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import styles from "../static/styles/WeeklyTournamentJoin.module.css";
import weeklyTournamentBg from "../static/img/WeeklyTournaments/imagePubgWeeklyTournament.avif";

import { trackTournamentSignup } from "../utils/analytics";
import { fetchTournamentById, squadTournamentEntry } from "../api/tournaments";

import MessagePopup from "../components/MessagePopup";
import ArrowLeft from "../static/img/backArrow.png";

const createEmptyPlayers = () =>
  Array.from({ length: 4 }, () => ({
    phone: "",
    uid: "",
    nick: "",
  }));

const normalizePhoneNumber = (value) => {
  const digits = value.replace(/\D/g, "");

  if (/^03\d{9}$/.test(digits)) {
    return `92${digits.slice(1)}`;
  }

  if (/^923\d{9}$/.test(digits)) {
    return digits;
  }

  return "";
};

const formatPhoneInput = (value) => {
  const digits = value.replace(/\D/g, "");

  if (digits.startsWith("03")) {
    return digits.slice(0, 11);
  }

  return digits.slice(0, 12);
};

const isValidPhoneNumber = (value) => Boolean(normalizePhoneNumber(value));

const getDuplicateIndexes = (values) => {
  const seen = new Map();
  const duplicates = new Set();

  values.forEach((value, index) => {
    if (!value) return;

    if (seen.has(value)) {
      duplicates.add(seen.get(value));
      duplicates.add(index);
    } else {
      seen.set(value, index);
    }
  });

  return duplicates;
};

const getApiMessage = (response, fallback) => {
  const message =
    response?.data?.message ||
    response?.message ||
    response?.errors ||
    response?.error;

  if (Array.isArray(message)) {
    return message.join("\n");
  }

  if (message && typeof message === "object") {
    return Object.values(message).flat().join("\n");
  }

  return message || fallback;
};

const SquadTournamentJoin = ({ isJcUser }) => {
  const [searchParams] = useSearchParams();
  const tournamentId = searchParams.get("tournamentId");

  const [tournamentData, setTournamentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEnded, setIsEnded] = useState(false);
  const [makeEntryLoading, setMakeEntryLoading] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [shouldRedirectOnPopupClose, setShouldRedirectOnPopupClose] =
    useState(false);
  const [teamName, setTeamName] = useState("");
  const [players, setPlayers] = useState(createEmptyPlayers);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");

  const navigate = useNavigate();
  const joinedPlayers = tournamentData?.entries?.length || 0;
  const prizePool = Number(tournamentData?.prize || 0);

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      if (!tournamentId) {
        setError("Tournament ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await fetchTournamentById(tournamentId);
        setTournamentData(data);
        setIsEnded(data.status > 2);
        setError(null);
      } catch (err) {
        setError(err.message || "Failed to load tournament details");
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentDetails();
  }, [tournamentId]);

  useEffect(() => {
    document.title = "Join Squad Tournament | Jazz Esl";
  }, []);

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handlePlayerChange = (index, field, value) => {
    let nextValue = value;

    if (field === "phone") {
      nextValue = formatPhoneInput(value);
    }

    if (field === "uid") {
      nextValue = value.replace(/\D/g, "").slice(0, 13);
    }

    setPlayers((prev) =>
      prev.map((player, playerIndex) =>
        playerIndex === index ? { ...player, [field]: nextValue } : player,
      ),
    );
    clearFieldError(`player_${index}_${field}`);
    setFormError("");
  };

  const handleTeamNameChange = (value) => {
    setTeamName(value.slice(0, 50));
    clearFieldError("teamName");
    setFormError("");
  };

  const validateForm = () => {
    const nextErrors = {};
    let hasMissingFields = false;
    let hasInvalidTeamName = false;
    let hasInvalidPhone = false;
    let hasInvalidUid = false;
    let hasDuplicatePlayerData = false;

    if (!teamName.trim()) {
      nextErrors.teamName = true;
      hasMissingFields = true;
    } else if (teamName.trim().length < 3) {
      nextErrors.teamName = true;
      hasInvalidTeamName = true;
    }

    players.forEach((player, index) => {
      if (!player.phone.trim()) {
        nextErrors[`player_${index}_phone`] = true;
        hasMissingFields = true;
      } else if (!isValidPhoneNumber(player.phone)) {
        nextErrors[`player_${index}_phone`] = true;
        hasInvalidPhone = true;
      }

      if (!player.uid.trim()) {
        nextErrors[`player_${index}_uid`] = true;
        hasMissingFields = true;
      } else if (!/^\d{6,13}$/.test(player.uid.trim())) {
        nextErrors[`player_${index}_uid`] = true;
        hasInvalidUid = true;
      }

      if (!player.nick.trim()) {
        nextErrors[`player_${index}_nick`] = true;
        hasMissingFields = true;
      }
    });

    const duplicatePhones = getDuplicateIndexes(
      players.map((player) => normalizePhoneNumber(player.phone)),
    );
    const duplicateUids = getDuplicateIndexes(
      players.map((player) => player.uid.trim()),
    );
    const duplicateNicks = getDuplicateIndexes(
      players.map((player) => player.nick.trim().toLowerCase()),
    );

    duplicatePhones.forEach((index) => {
      nextErrors[`player_${index}_phone`] = true;
      hasDuplicatePlayerData = true;
    });
    duplicateUids.forEach((index) => {
      nextErrors[`player_${index}_uid`] = true;
      hasDuplicatePlayerData = true;
    });
    duplicateNicks.forEach((index) => {
      nextErrors[`player_${index}_nick`] = true;
      hasDuplicatePlayerData = true;
    });

    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const errorMessages = [];

      if (hasMissingFields) {
        errorMessages.push(
          "Please fill the team name and all four players' phone number, UID, and nickname.",
        );
      }

      if (hasInvalidPhone) {
        errorMessages.push(
          "Phone numbers must start with 03 and have 11 digits, or start with 92 and have 12 digits.",
        );
      }

      if (hasInvalidTeamName) {
        errorMessages.push("Team name must be at least 3 characters.");
      }

      if (hasInvalidUid) {
        errorMessages.push("PUBG UID must be 6 to 13 numbers.");
      }

      if (hasDuplicatePlayerData) {
        errorMessages.push(
          "Each player must have a unique phone number, UID, and nickname.",
        );
      }

      setFormError(errorMessages.join(" "));
      return false;
    }

    setFormError("");
    return true;
  };

  const handleRegister = async () => {
    if (makeEntryLoading) return;

    setFormError("");

    if (!validateForm()) return;

    setMakeEntryLoading(true);

    const params = {
      id: tournamentId,
      team_name: teamName.trim(),
      players: players.map((player) => ({
        msisdn: normalizePhoneNumber(player.phone),
        pubg_id: player.uid.trim(),
        pubg_nick: player.nick.trim(),
      })),
    };

    try {
      const response = await squadTournamentEntry(params);

      if (response?.success) {
        trackTournamentSignup(tournamentData.tourn_name, tournamentId, "Squad");
        setPopupMessage(
          getApiMessage(response, "Your squad is successfully registered."),
        );
        setShouldRedirectOnPopupClose(true);
      } else if (response?.data?.already_submited) {
        setPopupMessage(
          getApiMessage(response, "Your squad is already registered for this tournament."),
        );
        setShouldRedirectOnPopupClose(false);
      } else {
        setPopupMessage(
          getApiMessage(response, "Failed to register squad for the tournament."),
        );
        setShouldRedirectOnPopupClose(false);
      }
    } catch (error) {
      setPopupMessage(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "Failed to register squad for the tournament.",
      );
      setShouldRedirectOnPopupClose(false);
    } finally {
      setMakeEntryLoading(false);
      setIsPopupOpen(true);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading tournament details...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!tournamentData) {
    return <div className={styles.error}>No tournament data available</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.bgGlow}></div>

      <div className={styles.navigationHeader}>
        <div className={styles.navigationBack} onClick={() => navigate(-1)}>
          <img src={ArrowLeft} alt="<" />
        </div>
        <div className={styles.navigationTitle}>
          <p>Join</p>
          <h2 className={styles.heading}>squad tournament</h2>
        </div>
      </div>

      <div className={styles.tournamentCard}>
        <div className={styles.cardTop}>
          <div className={styles.gameIcon}>
            <img
              src={tournamentData?.banner || weeklyTournamentBg}
              alt={tournamentData?.tourn_name || "Tournament"}
            />
          </div>

          <div>
            <div className={styles.livePlayers}>
              LIVE 
            </div>
            <h2>{tournamentData?.tourn_name}</h2>
            <p>Squad Tournament</p>
          </div>
        </div>

        <div className={styles.statsRow}>
          <div>
            <span>{prizePool}</span>
            <p>WINNER PRIZE</p>
          </div>

          <div>
            {isJcUser ? (
              <span>FREE</span>
            ) : (
              <span>
                {tournamentData.entryFee != null
                  ? `${tournamentData.entryFee}`
                  : tournamentData.isEntryFree === false
                    ? "Entry N/A"
                    : "Free"}
              </span>
            )}
            <p>{isJcUser ? "ENTRY" : "ENTRY FEE"}</p>
          </div>

          {/* <div>
            <span>{joinedPlayers}</span>
            <p>JOINED</p>
          </div> */}

        </div>
      </div>

      <h3 className={styles.sectionTitle}>PRIZE DISTRIBUTION</h3>

      <div className={styles.prizeBox}>
        <div className={styles.prizeRow}>
          <div className={styles.prizeLeft}>
            <div className={`${styles.rankBadge} ${styles.gold}`}>1ST</div>
            <span>Winning Squad</span>
          </div>
          <strong>{tournamentData?.prize} Credits</strong>
        </div>
      </div>

      <div className={styles.rulesBox}>
        <h4>QUICK RULES</h4>
        <ul>
          <li>Only the squad captain should enter and submit the team data.</li>
          <li>Enter exactly four squad members with their phone number, UID, and nickname.</li>
          <li>The entry fee will be deducted equally from all squad members' accounts.</li>
          <li>The reward will be distributed equally among all squad members.</li>
          <li>Only one squad will win the tournament reward.</li>
        </ul>
      </div>

      <div className={styles.formSection}>
        <div className={styles.squadInstructions}>
          Fill this form as the squad captain. Add the team name, then enter all
          four players' phone number, UID, and nickname before submitting.
        </div>

        <input
          className={`${styles.input} ${fieldErrors.teamName ? styles.errorInput : ""}`}
          placeholder="Enter Team Name"
          value={teamName}
          maxLength={50}
          onChange={(event) => handleTeamNameChange(event.target.value)}
        />

        {players.map((player, index) => (
          <div className={styles.squadMemberCard} key={`player-${index}`}>
            <h4 className={styles.squadMemberTitle}>
              Player {index + 1}
              {index === 0 ? " (Captain)" : ""}
            </h4>
            <div className={styles.squadFieldGrid}>
              <input
                type="text"
                inputMode="numeric"
                className={`${styles.input} ${
                  fieldErrors[`player_${index}_phone`] ? styles.errorInput : ""
                }`}
                placeholder="Phone Number ( Must be ESL user )"
                value={player.phone}
                onChange={(event) =>
                  handlePlayerChange(index, "phone", event.target.value)
                }
              />
              <input
                className={`${styles.input} ${
                  fieldErrors[`player_${index}_uid`] ? styles.errorInput : ""
                }`}
                placeholder="UID"
                value={player.uid}
                onChange={(event) =>
                  handlePlayerChange(index, "uid", event.target.value)
                }
              />
              <input
                className={`${styles.input} ${
                  fieldErrors[`player_${index}_nick`] ? styles.errorInput : ""
                }`}
                placeholder="Nickname"
                value={player.nick}
                onChange={(event) =>
                  handlePlayerChange(index, "nick", event.target.value)
                }
              />
            </div>
          </div>
        ))}

        {formError && <p className={styles.formError}>{formError}</p>}

        <button
          className={styles.registerButton}
          onClick={handleRegister}
          disabled={makeEntryLoading || isEnded || tournamentData.status === 2}
        >
          {makeEntryLoading
            ? "Please wait..."
            : tournamentData.status >= 2
              ? "Registrations Closed"
              : "Register Squad"}
        </button>
      </div>

      <MessagePopup
        isOpen={isPopupOpen}
        message={popupMessage}
        onClose={() => {
          setIsPopupOpen(false);
          if (shouldRedirectOnPopupClose) {
            navigate("/tournaments");
          }
        }}
      />
    </div>
  );
};

export default SquadTournamentJoin;
